import { Component, Inject } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

/** Matches the MaximumLength rules on AddFavoriteCommand and UpdateFavoriteCommand. */
export const FAVORITE_LABEL_MAX_LENGTH = 200;

export interface BookmarkPromptDialogData {
    /** 'create' saves a new favourite, 'edit' updates an existing one. */
    mode: 'create' | 'edit';
    label: string;
    icon: string;
}

export interface BookmarkPromptDialogResult {
    label: string;
    icon: string;
}

interface BookmarkIconOption {
    label: string;
    value: string;
}

@Component({
    selector: 'bookmark-prompt-dialog',
    templateUrl: './bookmark-prompt-dialog.component.html',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
    ],
})
export class BookmarkPromptDialogComponent {
    readonly iconOptions: BookmarkIconOption[] = [
        { label: 'Star', value: 'heroicons_solid:star' },
        { label: 'Dashboard', value: 'mat_outline:dashboard' },
        { label: 'Events', value: 'mat_outline:event' },
        { label: 'Exhibitors', value: 'mat_outline:storefront' },
        { label: 'Products', value: 'mat_outline:category' },
        { label: 'Suppliers', value: 'mat_outline:local_shipping' },
        { label: 'Users', value: 'mat_outline:people' },
        { label: 'Reports', value: 'mat_outline:assessment' },
    ];

    form: FormGroup;
    maxLabelLength: number = FAVORITE_LABEL_MAX_LENGTH;

    constructor(
        private _fb: FormBuilder,
        public dialogRef: MatDialogRef<
            BookmarkPromptDialogComponent,
            BookmarkPromptDialogResult
        >,
        @Inject(MAT_DIALOG_DATA) public data: BookmarkPromptDialogData
    ) {
        this._ensureIconOption(data.icon);

        this.form = this._fb.group({
            label: [
                data.label,
                [
                    Validators.required,
                    Validators.maxLength(FAVORITE_LABEL_MAX_LENGTH),
                ],
            ],
            icon: [
                data.icon || this.iconOptions[0].value,
                [Validators.required],
            ],
        });
    }

    get isEditMode(): boolean {
        return this.data.mode === 'edit';
    }

    get pageTitle(): string {
        return this.isEditMode ? 'Edit Favourite' : 'Add Favourite';
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.dialogRef.close({
            label: this.form.get('label').value.trim(),
            icon: this.form.get('icon').value,
        });
    }

    /**
     * The eight curated options don't cover every navigation icon, so a favourite
     * whose icon falls outside them keeps it as a first option rather than opening
     * with an empty select.
     */
    private _ensureIconOption(icon: string): void {
        if (!icon || this.iconOptions.some((option) => option.value === icon)) {
            return;
        }

        this.iconOptions.unshift({
            label: 'Current icon',
            value: icon,
        });
    }
}
